import React, { useEffect, useState } from 'react';
import CallController from '../API/CallController';
import '../scss/ManageCategories.scss';

interface Category {
  id: number;
  name: string;
  parentCategoryId?: number | null;
  parentCategoryName?: string | null;
}

const ManageCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [name, setName] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(CallController.GetAllCategories);
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      console.error('Kunde inte hämta kategorier:', err);
    }
  };

  const cleanErrorMessage = (msg: string) => {
    let cleaned = msg;
    if (cleaned.toLowerCase().startsWith('error')) {
      cleaned = cleaned.substring(cleaned.indexOf(':') + 1).trim();
    }
    return cleaned;
  };

  const handleAddCategory = async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      alert('Du måste logga in för att lägga till kategori.');
      return;
    }

    const body = {
      name: name,
      parentCategoryId: parentCategoryId ? Number(parentCategoryId) : null,
    };

    try {
      const res = await fetch(CallController.AddCategory, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        setErrorMessage(cleanErrorMessage(data.error || 'Kunde inte lägga till kategori'));
        return;
      }
      // Clear input fields and refresh categories after successful addition
      setName('');
      setParentCategoryId('');
      await fetchCategories();
    } catch (err: any) {
      setErrorMessage('Fel vid tillägg.');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    const token = localStorage.getItem('token');

    if (!token) {
      alert('Du måste logga in för att kunna radera kategori.');
      return;
    }
    try {
      const res = await fetch(`${CallController.DeleteCategory}/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg);
      }
      // Refresh categories after successful deletion
      await fetchCategories();
    } catch (err: any) {
      try {
        const errObj = JSON.parse(err.message);
        setErrorMessage(errObj.error || 'Ett fel uppstod');
      } catch {
        setErrorMessage(err.message || 'Ett fel uppstod');
      }


    }

  };

  return (
    <div className="admin-form">
      <h2>Hantera kategorier</h2>

      {errorMessage && <div className="error-message">{errorMessage}</div>}

      <div>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Namn på ny kategori"
        />
        <select value={parentCategoryId} onChange={e => setParentCategoryId(e.target.value)}>
          <option value="">Huvudkategori</option>
          {categories
            .filter(cat => cat.parentCategoryId === null)
            .map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
        </select>

        <button onClick={handleAddCategory}>Lägg till kategori</button>
      </div>

      <h3>Befintliga kategorier</h3>
      <ul className="category-list">
        {categories
          .filter(cat => cat.parentCategoryId === null)
          .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
          .flatMap(mainCat => [
            // Huvudkategori
            <li key={mainCat.id} className="main-category">
              <strong>{mainCat.name}</strong>
              <button onClick={() => handleDeleteCategory(mainCat.id)}>
                Radera
              </button>
            </li>,

            // Underkategorier
            ...categories
              .filter(cat => cat.parentCategoryId === mainCat.id)
              .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
              .map(subCat => (
                <li key={subCat.id} className="sub-category">
                  {subCat.name}
                  <button onClick={() => handleDeleteCategory(subCat.id)}>
                    Radera
                  </button>
                </li>
              ))
          ])}
      </ul>
    </div>
  );
};

export default ManageCategories;
